import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stack,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      await login();
    } catch (error) {
      // Login failed
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <Card
          elevation={2}
          sx={{
            borderRadius: 2,
            p: 2,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              PSEL Chatbot
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Admin Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in with your Microsoft work account
            </Typography>
          </Box>

          <CardContent sx={{ pt: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              {/* Official Microsoft Sign-in Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleMicrosoftLogin}
                disabled={loading}
                sx={{
                  py: 1.5,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #8c8c8c',
                  color: '#5e5e5e',
                  '&:hover': {
                    backgroundColor: '#f3f2f1',
                    border: '1px solid #8c8c8c',
                  },
                  '&:disabled': {
                    backgroundColor: '#f3f2f1',
                    border: '1px solid #c8c6c4',
                    color: '#a19f9d',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                }}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" gap={1} width="100%" justifyContent="center">
                    <CircularProgress size={16} sx={{ color: '#5e5e5e' }} />
                    <span>Signing in...</span>
                  </Box>
                ) : (
                  <>
                    {/* Official Microsoft Logo SVG */}
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="12" y="1" width="9" height="9" fill="#00a4ef"/>
                      <rect x="1" y="12" width="9" height="9" fill="#ffb900"/>
                      <rect x="12" y="12" width="9" height="9" fill="#7fba00"/>
                    </svg>
                    <span style={{ marginLeft: '8px' }}>Sign in with Microsoft</span>
                  </>
                )}
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 2, fontSize: '12px' }}
              >
                Use your work account to access this portal
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            PSEL Chatbot Admin Panel v1.0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;